import _ from 'lodash';
import { defineStore } from 'pinia';

import { AgentStatus, AgentStatusSettings } from '@/types';
import { DEFAULT_STATUS_KEY } from '@/constants';

export const useAgentStatusSettingsStore = defineStore('HITL-Agent-Status-Settings', {
  state: () => {
    return {
      isInitializing: false,
      /** Settings object that matches data in the DB */
      settings: {} as AgentStatusSettings,
      statusesById: {} as Record<string, AgentStatus>,
      defaultStatusKey: DEFAULT_STATUS_KEY,
      showSidebar: false,
      /** Are we creating a new status or updating existing? */
      targetKey: '',
      /**
       * Temporary value that is being edited in the sidebar.
       * Since status key can be changed, original key is stored above.
       * Target item has ready-to-save values.
       */
      targetItem: {} as AgentStatus,
      isUpdatingId: '',
      isUpdatingType: '' as '' | 'items' | 'create' | 'update' | 'delete',
    };
  },
  getters: {
    isUpdating: state => !!state.isUpdatingType,
    /** Add a "vforkey" property required by OrList */
    statusOptions: state => {
      const statuses = Object.values(state.statusesById)
        .map(status => ({ vforkey: status.key, ...status }));
      return statuses.sort((a, b) => (a.order || 0) - (b.order || 0));
    },
    actionsType: state => {
      switch (true) {
        case state.showSidebar && !state.targetKey:
          return 'create';
        case state.showSidebar && !!state.targetKey:
          return 'update';
        default:
          return '';
      }
    },
  },
});

export const useAgentStatusSettingsSetters = () => {
  const store = useAgentStatusSettingsStore();

  const onOpenCreateSidebar = () => {
    store.targetKey = '';
    store.showSidebar = true;
  };

  const onOpenUpdateSidebar = (key: string) => {
    store.targetKey = key;
    store.targetItem = _.cloneDeep(store.statusesById[key]);
    store.showSidebar = true;
  };

  const onTargetItemChange = (status: AgentStatus) => {
    const label = status.label?.replace(/\s+/g, ' ').trim();
    const icon = status.icon?.replace(/\s+/g, ' ').trim();
    store.targetItem = { ..._.cloneDeep(status), label, icon };
  };

  const onCancelTargetItem = () => {
    store.targetKey = '';
    store.targetItem = {} as AgentStatus;
    store.showSidebar = false;
  };

  return {
    onOpenCreateSidebar,
    onOpenUpdateSidebar,
    onTargetItemChange,
    onCancelTargetItem,
  };
};
